/**
 * Week / month / quarter re-bucketing for the exec day series (quick-261104-ia3).
 * Pure. Weeks start Sunday (same as the old MAIN-01/04 tiles). The current,
 * in-progress period is kept but flagged `partial` so charts can fade it.
 */
import type { ExecDayPoint } from '@saledash/shared';

export type Grain = 'week' | 'month' | 'quarter';

export interface PeriodBucket {
  key: string;
  label: string;
  c: number;
  amt: number;
  partial: boolean;
}

/** How many periods each grain shows. */
export const GRAIN_SPAN: Record<Grain, number> = { week: 13, month: 12, quarter: 5 };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad2(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n);
}

function isoOf(d: Date): string {
  return `${String(d.getUTCFullYear())}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/** Period key for an ISO day. */
export function periodKey(iso: string, grain: Grain): string {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  if (grain === 'month') return `${String(y)}-${pad2(m)}`;
  if (grain === 'quarter') return `${String(y)}-Q${String(Math.floor((m - 1) / 3) + 1)}`;
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return isoOf(d);
}

/** Human label for a period key. */
export function periodLabel(key: string, grain: Grain): string {
  if (grain === 'month') {
    const m = Number(key.slice(5, 7));
    return `${MONTHS[m - 1] ?? key} ${key.slice(2, 4)}`;
  }
  if (grain === 'quarter') return `${key.slice(5)} ${key.slice(2, 4)}`;
  return `${String(Number(key.slice(5, 7)))}/${String(Number(key.slice(8, 10)))}`;
}

/** Step a period key back by one. */
function prevKey(key: string, grain: Grain): string {
  if (grain === 'month') {
    const y = Number(key.slice(0, 4));
    const m = Number(key.slice(5, 7));
    return m === 1 ? `${String(y - 1)}-12` : `${String(y)}-${pad2(m - 1)}`;
  }
  if (grain === 'quarter') {
    const y = Number(key.slice(0, 4));
    const q = Number(key.slice(6));
    return q === 1 ? `${String(y - 1)}-Q4` : `${String(y)}-Q${String(q - 1)}`;
  }
  const d = new Date(`${key}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 7);
  return isoOf(d);
}

/**
 * Bucket a day series into the last `span` periods ending with the period that
 * contains `today` (zero-filled). Periods that start before `since` are dropped
 * so a short history never shows false zeros.
 */
export function bucketSeries(
  points: readonly ExecDayPoint[],
  grain: Grain,
  today: string,
  since?: string,
  span: number = GRAIN_SPAN[grain],
): PeriodBucket[] {
  const sums = new Map<string, { c: number; amt: number }>();
  for (const p of points) {
    const k = periodKey(p.d, grain);
    const e = sums.get(k) ?? { c: 0, amt: 0 };
    e.c += p.c;
    e.amt += p.amt ?? 0;
    sums.set(k, e);
  }
  const current = periodKey(today, grain);
  const sinceKey = since ? periodKey(since, grain) : null;
  const keys: string[] = [];
  let k = current;
  for (let i = 0; i < span; i++) {
    if (sinceKey && k < sinceKey) break;
    keys.unshift(k);
    k = prevKey(k, grain);
  }
  return keys.map((key) => ({
    key,
    label: periodLabel(key, grain),
    c: sums.get(key)?.c ?? 0,
    amt: sums.get(key)?.amt ?? 0,
    partial: key === current,
  }));
}

/** Today as ISO (local calendar day). */
export function todayIso(now: Date = new Date()): string {
  return `${String(now.getFullYear())}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}
